import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, X } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogTrigger } from './ui/Dialog';
import { apiFetch } from '../lib/api';
import { ApiError, type ContactDetail, type ContactEditInput } from '../types/api';

const FIELD_KEY = /^[a-z][a-z0-9_]{0,39}$/;

interface FieldRow {
  key: string;
  value: string;
}

/**
 * Editor+: change a contact by hand. Name, language, country and custom fields only.
 * Consent is not editable here: it can be withdrawn (unsubscribe) but never granted by hand.
 */
export function EditContactDialog({
  contact,
  fields,
}: {
  contact: ContactDetail;
  fields: Record<string, string>;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [language, setLanguage] = useState('');
  const [country, setCountry] = useState('');
  const [rows, setRows] = useState<FieldRow[]>([]);

  function reset() {
    setFirstName(contact.firstName ?? '');
    setLastName(contact.lastName ?? '');
    setLanguage(contact.language ?? '');
    setCountry(contact.country ?? '');
    setRows(Object.entries(fields).map(([key, value]) => ({ key, value })));
  }

  const save = useMutation({
    mutationFn: (input: ContactEditInput) =>
      apiFetch(`/contacts/${contact.id}`, { method: 'PATCH', body: input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contact', contact.id] });
      setOpen(false);
    },
  });

  const keys = rows.map((r) => r.key.trim());
  const rowsValid =
    keys.every((k) => FIELD_KEY.test(k)) &&
    new Set(keys).size === keys.length &&
    rows.every((r) => r.value.length <= 200);

  function submit() {
    const input: ContactEditInput = {};
    const text = (v: string) => (v.trim() === '' ? null : v.trim());
    if (text(firstName) !== (contact.firstName ?? null)) input.firstName = text(firstName);
    if (text(lastName) !== (contact.lastName ?? null)) input.lastName = text(lastName);
    if (text(language) !== (contact.language ?? null)) input.language = text(language);
    if (text(country) !== (contact.country ?? null)) input.country = text(country);
    const custom: Record<string, string | null> = {};
    for (const key of Object.keys(fields)) {
      if (!keys.includes(key)) custom[key] = null;
    }
    for (const r of rows) {
      const key = r.key.trim();
      if (fields[key] !== r.value) custom[key] = r.value;
    }
    if (Object.keys(custom).length > 0) input.customFields = custom;
    if (Object.keys(input).length === 0) {
      setOpen(false);
      return;
    }
    save.mutate(input);
  }

  const errorText =
    save.error instanceof ApiError && save.error.message
      ? save.error.message
      : 'The contact could not be saved. Please try again.';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) reset();
        else save.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="secondary">
          <Pencil size={15} aria-hidden="true" />
          Edit contact
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Edit contact"
        description="Changes are written to the audit log. Consent cannot be changed here."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (rowsValid) submit();
          }}
        >
          {save.isError ? <Alert>{errorText}</Alert> : null}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-first-name">First name</Label>
              <Input
                id="edit-first-name"
                value={firstName}
                maxLength={100}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-last-name">Last name</Label>
              <Input
                id="edit-last-name"
                value={lastName}
                maxLength={100}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-language">Language (code, e.g. da)</Label>
              <Input
                id="edit-language"
                value={language}
                maxLength={20}
                onChange={(e) => setLanguage(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-country">Country (code, e.g. DK)</Label>
              <Input
                id="edit-country"
                value={country}
                maxLength={10}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
          </div>

          <fieldset className="m-0 p-0 border-0 flex flex-col gap-2">
            <legend className="text-sm font-medium mb-1">Custom fields</legend>
            {rows.map((r, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <Label htmlFor={`field-key-${i}`}>Field name {i + 1}</Label>
                  <Input
                    id={`field-key-${i}`}
                    value={r.key}
                    maxLength={40}
                    className="font-mono"
                    onChange={(e) =>
                      setRows(rows.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))
                    }
                  />
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                  <Label htmlFor={`field-value-${i}`}>Value {i + 1}</Label>
                  <Input
                    id={`field-value-${i}`}
                    value={r.value}
                    maxLength={200}
                    onChange={(e) =>
                      setRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))
                    }
                  />
                </div>
                <button
                  type="button"
                  aria-label={`Remove field ${i + 1}`}
                  className="grid h-9 w-9 place-items-center rounded-md text-muted hover:bg-border-soft hover:text-text"
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              </div>
            ))}
            {!rowsValid ? (
              <p className="m-0 text-xs text-muted">
                Field names are unique, start with a letter and use a-z, 0-9 or _.
              </p>
            ) : null}
            <div>
              <Button
                type="button"
                variant="secondary"
                disabled={rows.length >= 20}
                onClick={() => setRows([...rows, { key: '', value: '' }])}
              >
                <Plus size={15} aria-hidden="true" />
                Add field
              </Button>
            </div>
          </fieldset>

          <DialogFooter>
            <DialogClose>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!rowsValid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
